import { Trans, useLingui } from '@lingui/react/macro'
import { App, Button } from 'antd'
import { useEffect, useRef } from 'react'

import { fetchVersion } from './fetch-version'
import { isVersionMismatch } from './version-check'

const POLL_MS = 5 * 60 * 1000
const VISIBILITY_THROTTLE_MS = 30_000
const NOTICE_KEY = 'app-update'

export function AppUpdateChecker() {
  const { notification } = App.useApp()
  const { t } = useLingui()
  const currentRef = useRef<number | null>(null)
  const lastVisibilityCheckAt = useRef(0)

  useEffect(() => {
    const notify = () => {
      notification.info({
        key: NOTICE_KEY,
        title: t`New version available`,
        description: t`A new version of the app is available. Refresh to update.`,
        duration: 0,
        placement: 'bottomLeft',
        actions: [
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(NOTICE_KEY)
              window.location.reload()
            }}
          >
            <Trans>Refresh</Trans>
          </Button>,
        ],
      })
    }

    const check = async () => {
      const remote = await fetchVersion()
      if (remote == null) return

      if (currentRef.current == null) {
        currentRef.current = remote
        return
      }

      if (!isVersionMismatch(currentRef.current, remote)) return
      notify()
    }

    void check()
    const intervalId = window.setInterval(() => void check(), POLL_MS)

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastVisibilityCheckAt.current < VISIBILITY_THROTTLE_MS) return
      lastVisibilityCheckAt.current = now
      void check()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const onPreloadError = (event: Event) => {
      event.preventDefault()
      notify()
    }
    window.addEventListener('vite:preloadError', onPreloadError)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('vite:preloadError', onPreloadError)
    }
  }, [notification, t])

  return null
}
